import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { CACHE_TIME, HTTP_STATUS, RETRY } from '@/constants/app';
import { translateApiError } from '@/lib/utils/errorTranslator';
import { showError } from '@/lib/utils/toast';
import type { ApiError } from '@/types/api';

const handleQueryError = (error: unknown) => {
  const apiError = error as ApiError;

  if (apiError?.status === HTTP_STATUS.UNAUTHORIZED) return;
  if (apiError?.status === HTTP_STATUS.NOT_FOUND) return;

  if (apiError?.message) {
    const translatedMessage = translateApiError(apiError.message);
    showError(translatedMessage);
  }

  if (import.meta.env.DEV) {
    console.error('Query Error:', error);
  }
};

const handleMutationError = (error: unknown) => {
  const apiError = error as ApiError;

  if (apiError?.status === HTTP_STATUS.UNAUTHORIZED) return;

  if (apiError?.message) {
    const translatedMessage = translateApiError(apiError.message);
    showError(translatedMessage);
  }

  if (import.meta.env.DEV) {
    console.error('Mutation Error:', error);
  }
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status;

        if (
          status &&
          status >= RETRY.NO_RETRY_STATUS_MIN &&
          status < RETRY.NO_RETRY_STATUS_MAX
        ) {
          return false;
        }

        return failureCount < RETRY.MAX_QUERY_RETRIES;
      },
      retryDelay: attemptIndex =>
        Math.min(
          CACHE_TIME.BASE_RETRY_DELAY * 2 ** attemptIndex,
          CACHE_TIME.MAX_RETRY_DELAY,
        ),
    },
    mutations: {
      retry: false,
    },
  },
});

export default queryClient;
